<script setup lang="ts">
import { computed } from 'vue';
import { useThemeOverrides } from '@apparatix/composables';
import DataTable, { DataTableRowClickEvent } from 'primevue/datatable';
import Column from 'primevue/column';
import Badge from 'primevue/badge';
import { Section, Typography } from '@apparatix/library/core';
import type { DetailTabName, ForwardNavigable } from './types';

type Props = {
  campaignId: number | null;
  campaignName: string | null;
  campaignStatus: string | null;
  campaignType: 'Lead' | 'Opportunity' | null;
  campaignBudgetDescription: string | null;
  campaignCreatedDate: string | null;
  campaignAge: number | null;
  tabId?: DetailTabName;
}

const props = defineProps<Props>();
const emit = defineEmits<ForwardNavigable>();

const { datatable } = useThemeOverrides();

const rows = computed(() => (props.campaignId ? [{
  id: props.campaignId,
  name: props.campaignName,
  status: props.campaignStatus,
  type: props.campaignType,
  budgetDescription: props.campaignBudgetDescription,
  createdDate: props.campaignCreatedDate,
  age: props.campaignAge,
}] : []));

const onRowClick = (e: DataTableRowClickEvent) => {
  emit('navigate-forward', { view: e.data.type, id: e.data.id, tabId: props.tabId });
};

const rowClass = () => 'cursor-pointer';
</script>
<template>
  <div class="w-full flex flex-col gap-6">
    <Section class="mb-3" :footer="false">
      <DataTable
        :value="rows"
        table-style="min-width: 50rem;"
        class="no-left-padding"
        :dt="datatable.noLeftPadding"
        :row-class="rowClass"
        @row-click="onRowClick"
      >
        <template #header>
          <div class="flex items-center justify-between gap-4">
            <Typography variant="section-heading">
              Leads &#38; Opportunities
              <Badge :value="rows.length" severity="secondary" />
            </Typography>
          </div>
        </template>
        <template #empty>
          No lead or opportunity associated with this document.
        </template>
        <template v-if="rows.length > 0">
          <Column
            field="name"
            header="Name"
            sortable
            class="text-[var(--text-link-primary)] cursor-pointer"
          />
          <Column field="status" header="Status" sortable />
          <Column field="budgetDescription" header="Budget Details" sortable />
          <Column
            field="createdDate"
            header="Created"
            sortable
            :sort-field="'age'"
          />
          <Column field="age" header="Age (Days)" sortable />
        </template>
      </DataTable>
    </Section>
  </div>
</template>
